import React from 'react'
import { Link } from 'react-router-dom'
import { Code, ArrowLeft, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import { Button } from '../../components/ui/Button'

export const ApiDocumentationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/documentation" className="inline-flex items-center text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Documentation
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <Code className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              API Documentation
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Integrate Open1031 with your existing systems using our comprehensive REST API.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Authentication */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                  Authentication
                </h2>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  All API requests require authentication using Bearer tokens. Obtain your API key from your account settings.
                </p>
                <div className="bg-slate-900 dark:bg-slate-950 rounded-lg p-4 overflow-x-auto">
                  <code className="text-sm text-slate-100">
                    <span className="text-purple-400">curl</span>{' '}
                    <span className="text-blue-400">-H</span>{' '}
                    <span className="text-green-400">"Authorization: Bearer YOUR_API_KEY"</span>{' '}
                    <span className="text-yellow-400">https://api.open1031.com/v1/exchanges</span>
                  </code>
                </div>
              </div>

              {/* Endpoints */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                  Core Endpoints
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                      Exchanges
                    </h3>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">GET /v1/exchanges</code> - List all exchanges</li>
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">POST /v1/exchanges</code> - Create a new exchange</li>
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">GET /v1/exchanges/:id</code> - Get exchange details</li>
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">PUT /v1/exchanges/:id</code> - Update exchange</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                      Documents
                    </h3>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">GET /v1/documents</code> - List documents</li>
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">POST /v1/documents</code> - Upload document</li>
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">DELETE /v1/documents/:id</code> - Delete document</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                      Properties
                    </h3>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">GET /v1/properties/lookup</code> - Lookup property details</li>
                      <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">GET /v1/locations/autocomplete</code> - Address autocomplete</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Rate Limits */}
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                  Rate Limits
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  API requests are limited to 1000 requests per hour per API key. Rate limit headers are included 
                  in all responses to help you manage your usage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Need API Support?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Our developer team is here to help you integrate with Open1031.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/support">
              <Button className="bg-white text-[#69adba] hover:bg-slate-50">
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/documentation">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                View All Documentation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

